import * as React from "react"

import { Skeleton } from "@ibada/ui"

import { useTranslations } from "@/i18n"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { ProgressBar } from "@/features/finance/overview/components/progress-bar"
import { formatMoneySafe, formatPercent } from "@/features/finance/overview/lib/overview-format"
import { shareOfTotal, useGlobalBranchRows } from "@/features/finance/overview/lib/use-global-branch-rows"

/**
 * Where the church SPENDS, by branch.
 *
 * The mirror of Branch contribution, and a genuinely different question: a branch can bring little in and still
 * account for most of what goes out. Reading the two side by side is what shows whether the branches that earn
 * are the branches that spend — the shape of a subsidy, which no single total reveals.
 *
 * GLOBAL only. Shares divide by the SERVER's total-expense figure, never by a browser-side sum of the rows.
 */
export function BranchSpendShareWidget() {
  const t = useTranslations("finance.overview")
  const { rows, totalExpense, currency, isLoading, isError, isFetching, refetch } = useGlobalBranchRows()

  const data = React.useMemo(() => {
    const spenders = rows
      .map((row) => ({
        id: row.id,
        name: row.name,
        expense: row.expense,
        share: shareOfTotal(row.expense, totalExpense),
      }))
      .filter((row) => row.share > 0)
      .sort((a, b) => b.share - a.share)

    return { rows: spenders, currency, totalExpense }
  }, [rows, totalExpense, currency])

  useWidgetMenu({ refresh: refetch })

  return (
    <WidgetBoundary
      isLoading={isLoading}
      isError={isError}
      isFetching={isFetching}
      data={data}
      isEmpty={(d) => d.rows.length === 0}
      skeleton={<SpendSkeleton />}
      errorTitle={t("widgets.branchSpendShare.error")}
      onRetry={refetch}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.branchSpendShare.empty.title")}
    >
      {(d) => (
        <ul className='space-y-2.5'>
          {d.rows.map((row) => (
            <li key={row.id} className='space-y-1'>
              <div className='flex items-baseline justify-between gap-3 text-sm'>
                <span className='min-w-0 flex-1 truncate'>{row.name}</span>
                <span className='shrink-0 font-medium tabular-nums'>{formatMoneySafe(row.expense, d.currency)}</span>
                <span className='text-muted-foreground w-10 shrink-0 text-right text-xs tabular-nums'>
                  {formatPercent(row.share)}
                </span>
              </div>
              <ProgressBar value={row.share} tone='warning' aria-label={row.name} />
            </li>
          ))}
        </ul>
      )}
    </WidgetBoundary>
  )
}

function SpendSkeleton() {
  return (
    <div className='space-y-2.5'>
      {[65, 50, 35].map((w, index) => (
        <div key={index} className='space-y-1'>
          <div className='flex items-center justify-between'>
            <Skeleton className='h-3.5' style={{ width: `${w}%` }} />
            <Skeleton className='h-3.5 w-14' />
          </div>
          <Skeleton className='h-1.5 w-full' />
        </div>
      ))}
    </div>
  )
}
